// Observability.  /analytics/observability
//
// AI traces, route latency, graph retrieval, guardrails, dependency readiness
// and cache. This screen is about the platform, not about the client's scope
// signals; those live on the chatbot analytics screen.
//
// The counters below are counts, and a count of zero is a real measurement:
// nothing happened. The RATES are different. A rate whose denominator is zero
// was never measured, and printing it as 0.0% reads as a total failure that
// nobody observed. Two of them are gated for exactly that reason, the drift
// baseline and the LLM cache hit rate.

import { useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  SkeletonRows,
  Spinner,
  Table,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  DependencyList,
  KeyCount,
  errText,
  nf,
  num,
  objectRows,
  type DependenciesResponse,
} from "./shared";

interface RouteLatencyRow {
  route: string;
  average_latency_ms: number;
  count: number;
}

interface AiUsageRow {
  provider?: string;
  model?: string;
  total_tokens: number;
}

interface ObservabilityOverview {
  enabled: boolean;
  source: string;
  days: number;
  overview: Record<string, unknown>;
}

interface DriftAlert {
  metric: string;
  delta: number;
}

interface DriftOverview {
  status?: string;
  alerts?: DriftAlert[];
  latest?: Record<string, unknown>;
}

interface CacheEntry {
  name: string;
  prefix: string;
  entries: number;
}

interface CacheStats {
  note: string;
  total_entries: number;
  caches: CacheEntry[];
  llm_call_cache: {
    hits: number;
    misses: number;
    hit_rate: number;
    note: string;
    counted_at: string;
  };
}

const OBSERVABILITY_METRICS: [string, string][] = [
  ["Trace count", "trace_count"],
  ["Errors", "error_count"],
  ["Avg latency ms", "average_latency_ms"],
  ["Graph hits", "graph_hit_count"],
  ["Review backlog", "review_queue_backlog"],
  ["Active learning", "active_learning_item_count"],
  ["Low-confidence transcripts", "low_confidence_transcript_count"],
  ["Stale facts", "stale_directory_fact_count"],
  ["Last stale facts queued", "last_revalidation_created_count"],
  ["Guardrail blocks", "guardrail_block_review_count"],
  ["Retrieval misses", "retrieval_miss_count"],
  ["Graph sync issues", "graph_sync_issue_count"],
  ["Quality evals", "quality_eval_count"],
  ["Retrieval evals", "retrieval_eval_count"],
  ["Classification evals", "classification_eval_count"],
  ["Promoted evals", "promoted_eval_count"],
  ["Cache hits", "cache_hit_count"],
  ["Cache invalidations", "response_cache_invalidation_count"],
  ["Security events", "security_event_count"],
  ["Eval runs", "eval_run_count"],
];

export function ObservabilityView() {
  const toast = useToast();
  const [days, setDays] = useState(7);
  const [version, setVersion] = useState(0);
  const [runningDrift, setRunningDrift] = useState(false);
  const [drift, setDrift] = useState<DriftOverview | null>(null);

  const canRun = useSession().can("analytics", "write");

  const query = useAsync(async () => {
    const [overview, driftOverview] = await Promise.all([
      http.get<ObservabilityOverview>(`/observability/overview${qs({ days })}`),
      http.get<DriftOverview>("/observability/drift"),
    ]);
    setDrift(driftOverview);
    return overview;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, version]);

  // Dependency readiness and cache stats sit behind organizations:read, so an
  // operator with analytics but not organizations gets the rest of the page
  // rather than a broken one.
  const depsQuery = useAsync(() => http.get<DependenciesResponse>("/ops/dependencies"), [version]);
  const cacheQuery = useAsync(() => http.get<CacheStats>("/ops/cache/stats"), [version]);

  const overview = query.data?.overview;
  const enabled = Boolean(query.data?.enabled);

  const runDrift = async () => {
    setRunningDrift(true);
    try {
      const result = await http.post<{ status?: string; alerts?: DriftAlert[] }>(
        "/observability/drift/run",
        { suite: "runtime-drift", min_count: 20 }
      );
      setDrift({ status: result.status, alerts: result.alerts ?? [], latest: result });
      toast.push(
        result.status === "alert" ? "Drift alerts recorded" : "Drift check completed",
        result.status === "alert" ? "warning" : "good"
      );
      setVersion((v) => v + 1);
    } catch (err) {
      toast.push(errText(err, "Could not run the drift check"), "critical");
    } finally {
      setRunningDrift(false);
    }
  };

  const latestDrift =
    (drift?.latest as Record<string, unknown> | undefined) ??
    (overview?.latest_drift as Record<string, unknown> | undefined) ??
    {};
  const alerts = drift?.alerts ?? [];
  // A drift check that never ran has no baseline and no recent window. Drawing
  // "0 / 0" beside a status of not_run states two counts nobody counted.
  const driftRan = Object.keys(latestDrift).length > 0;

  const llmCache = cacheQuery.data?.llm_call_cache;
  const llmCacheCalls = (llmCache?.hits ?? 0) + (llmCache?.misses ?? 0);

  const latencyRows: RouteLatencyRow[] = Array.isArray(overview?.route_latency)
    ? (overview?.route_latency as RouteLatencyRow[])
    : [];
  const usageRows: AiUsageRow[] = Array.isArray(overview?.ai_usage)
    ? (overview?.ai_usage as AiUsageRow[])
    : [];

  const latencyColumns: Column<RouteLatencyRow>[] = [
    { key: "route", header: "Route", sortable: true },
    {
      key: "average_latency_ms",
      header: "Avg latency",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{(Number(r.average_latency_ms) || 0).toFixed(1)} ms</span>,
    },
    {
      key: "count",
      header: "Calls",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.count)}</span>,
    },
  ];

  const usageColumns: Column<AiUsageRow>[] = [
    { key: "provider", header: "Provider", sortable: true, render: (r) => r.provider || "unknown" },
    { key: "model", header: "Model", sortable: true, render: (r) => r.model || "unknown" },
    {
      key: "total_tokens",
      header: "Tokens",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.total_tokens)}</span>,
    },
  ];

  const cacheColumns: Column<CacheEntry>[] = [
    { key: "name", header: "Cache", sortable: true },
    { key: "prefix", header: "Key prefix", sortable: true },
    {
      key: "entries",
      header: "Entries",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.entries)}</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Observability</h1>
          <div className="sub">
            AI traces, latency, graph retrieval hits, guardrails, dependencies and eval rollups.
          </div>
        </div>
        <div className="page-actions">
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            aria-label="Window in days"
            style={{ width: 90 }}
            onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 7)))}
          />
          <button className="btn" disabled={query.loading} onClick={() => setVersion((v) => v + 1)}>
            {query.loading ? <Spinner label="Refreshing" /> : "⟲"} Refresh
          </button>
          {canRun && (
            <button className="btn" disabled={runningDrift} onClick={() => void runDrift()}>
              {runningDrift ? <Spinner label="Running" /> : "▷"} Run drift
            </button>
          )}
          <a className="btn" href="/chatbot/model-lab">
            Model Lab
          </a>
        </div>
      </div>

      {query.error && (
        <Banner tone="critical" traceId={query.error.traceId}>
          Could not load observability data: {query.error.message}
        </Banner>
      )}

      {!query.loading && !enabled ? (
        <Banner tone="warning">DuckDB telemetry is not enabled for this server.</Banner>
      ) : (
        <>
          <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
            {OBSERVABILITY_METRICS.map(([label, key]) => (
              <div className="stat" key={key}>
                <div className="label">{label}</div>
                <div className="value">
                  {query.loading ? (
                    <Spinner />
                  ) : (
                    num(overview, key).toFixed(key === "average_latency_ms" ? 1 : 0)
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="grid c3" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="card">
              <div className="row">
                <h3>Drift status</h3>
                <span className="spacer" />
                <Badge tone={drift?.status === "alert" ? "warning" : "neutral"}>
                  {drift?.status || "not_run"}
                </Badge>
              </div>
              {driftRan ? (
                <div className="row" style={{ marginTop: "var(--sp-3)" }}>
                  <div>
                    <div className="muted">Baseline</div>
                    <div className="strong num">{num(latestDrift, "baseline_count").toFixed(0)}</div>
                  </div>
                  <span className="spacer" />
                  <div>
                    <div className="muted">Recent</div>
                    <div className="strong num">{num(latestDrift, "recent_count").toFixed(0)}</div>
                  </div>
                </div>
              ) : (
                <div className="muted" style={{ marginTop: "var(--sp-3)" }}>
                  Baseline and recent windows: not measured. No drift check has run, so there are no
                  windows to compare.
                </div>
              )}
              <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                {alerts.slice(0, 3).map((alert) => (
                  <div className="row" key={`${alert.metric}-${alert.delta}`}>
                    <span className="truncate">{alert.metric}</span>
                    <span className="spacer" />
                    <strong className="num">{(Number(alert.delta) || 0).toFixed(3)}</strong>
                  </div>
                ))}
                {alerts.length === 0 && <div className="muted">No drift alerts recorded</div>}
              </div>
            </div>

            <div className="card">
              <h3>Graph backend</h3>
              <div style={{ marginTop: "var(--sp-3)" }}>
                <KeyCount rows={objectRows(overview?.graph_backend_counts)} empty="No graph retrieval yet" />
              </div>
            </div>

            <div className="card">
              <h3>Fallback reasons</h3>
              <div style={{ marginTop: "var(--sp-3)" }}>
                <KeyCount rows={objectRows(overview?.fallback_reason_counts)} empty="No fallbacks recorded" />
              </div>
            </div>

            <div className="card">
              <h3>Failures</h3>
              <div style={{ marginTop: "var(--sp-3)" }}>
                <KeyCount
                  rows={[
                    { name: "Sync", count: num(overview, "sync_failure_count") },
                    { name: "Extraction", count: num(overview, "extraction_failure_count") },
                    { name: "OCR", count: num(overview, "ocr_failure_count") },
                    { name: "Guardrail", count: num(overview, "guardrail_block_count") },
                  ]}
                  empty="No failures recorded"
                />
              </div>
            </div>
          </div>

          <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="card flush">
              <div className="card-head">
                <h3>Route latency</h3>
              </div>
              <Table
                columns={latencyColumns}
                rows={latencyRows}
                loading={query.loading}
                empty="No latency data"
              />
            </div>
            <div className="card flush">
              <div className="card-head">
                <h3>AI usage</h3>
              </div>
              <Table
                columns={usageColumns}
                rows={usageRows}
                loading={query.loading}
                empty="No token usage yet"
              />
            </div>
          </div>

          <div className="grid c2">
            <div className="card">
              <div className="row">
                <h3>Dependency readiness</h3>
                <span className="spacer" />
                {depsQuery.data && (
                  <Badge tone={depsQuery.data.status === "ready" ? "good" : "critical"}>
                    {depsQuery.data.status}
                  </Badge>
                )}
              </div>
              {depsQuery.error ? (
                <Banner tone="warning" traceId={depsQuery.error.traceId}>
                  Dependency probes need the organizations permission: {depsQuery.error.message}
                </Banner>
              ) : depsQuery.loading ? (
                <SkeletonRows n={4} cols={2} />
              ) : (
                <div style={{ marginTop: "var(--sp-3)" }}>
                  <DependencyList rows={depsQuery.data?.dependencies ?? []} />
                </div>
              )}
            </div>

            <div className="card flush">
              <div className="card-head">
                <h3>Cache</h3>
                {cacheQuery.data && (
                  <span className="muted">
                    {nf.format(cacheQuery.data.total_entries)} entries ·{" "}
                    {/* No LLM call was looked up, so there is no hit rate. A 0.0%
                        here would read as a cache that never works. */}
                    {llmCacheCalls > 0
                      ? `${((llmCache?.hit_rate ?? 0) * 100).toFixed(1)}% LLM hit rate (${nf.format(
                          llmCache?.hits ?? 0
                        )} hits / ${nf.format(llmCache?.misses ?? 0)} misses)`
                      : "LLM hit rate not measured, no cacheable call yet"}
                  </span>
                )}
              </div>
              {cacheQuery.error ? (
                <div className="card-body">
                  <Banner tone="warning" traceId={cacheQuery.error.traceId}>
                    Cache stats need the organizations permission: {cacheQuery.error.message}
                  </Banner>
                </div>
              ) : (
                <Table
                  columns={cacheColumns}
                  rows={cacheQuery.data?.caches ?? []}
                  loading={cacheQuery.loading}
                  empty="No cache prefixes reported"
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
